// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createActionsHook,
	createStateHook,
	createStore,
	type Action,
} from '@atlassian/react-sweet-state';

import { autoBeforeEachTest } from '@atlassian/jira-platform-auto-before-each-test/src/auto-before-each-test.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { callWhenIdleWithTimeout } from './callWhenIdleWithTimeout.tsx';

type State = { renderMode: 'hydrateRoot'; isComplete: boolean } | { renderMode: 'createRoot' };
const DEFAULT_STATE: State = { renderMode: 'createRoot' };
let state: State = Object.freeze(DEFAULT_STATE);

// Even though this uses no hooks, we expose it as one to ensure it is only used during render
// (which is the only time we know if we're hydrating)
// For that reason, though, it's important that this never changes value
export const useIsHydrateRenderMode = (): boolean => state.renderMode === 'hydrateRoot';

export const useSsrPlaceholders = (): boolean =>
	state.renderMode !== 'hydrateRoot' || !fg('jfp-magma-data-ssr-placeholder-retain');

/**
 * Indicates that we are rendering React using `hydrateRoot`. This should be called once only early in init before any
 * rendering, since it's assumed to remain constant in {@link useIsHydrateRenderMode}
 */
export const setIsHydrationRenderMode = (): void => {
	state = { renderMode: 'hydrateRoot', isComplete: false };
};

/**
 * Indicates that hydration is finished. Ignored if not in hydrate root mode.
 */
export const reportHydrationEnd = (): void => {
	if (state.renderMode === 'hydrateRoot') {
		state.isComplete = true;
	}
};

export const getIsHydrationInProgress = (): boolean =>
	state.renderMode === 'hydrateRoot' && !state.isComplete;

type DebugInfo = {
	pendingIds: Record<string, string>;
	firstPendingCallAt: number | null;
	lastCompletionCallAt: number | null;
	addPendingSubTreeCallCount: number;
	completeSubTreeCallCount: number;
	unorderlyCompleteSubtreeCalls: number;
	completionAttempts: number;
	idleCallbackTimedOut: boolean;
	globalTimedOut: boolean;
};
let DEBUG_INFO: DebugInfo = {
	pendingIds: {},
	firstPendingCallAt: null,
	lastCompletionCallAt: null,
	addPendingSubTreeCallCount: 0,
	completeSubTreeCallCount: 0,
	unorderlyCompleteSubtreeCalls: 0,
	completionAttempts: 0,
	idleCallbackTimedOut: false,
	globalTimedOut: false,
};
export const getPendingSubtreeNames = (): string[] => {
	return Object.values(DEBUG_INFO.pendingIds);
};
export const getIsGlobalTimedOut = (): boolean => DEBUG_INFO.globalTimedOut;

type Unsubscribe = () => void;
let clearNextCallback: Unsubscribe | null = null;
const setOrReplaceNextCallback = (createCallback: () => Unsubscribe) => {
	if (clearNextCallback != null) {
		clearNextCallback();
	}
	clearNextCallback = createCallback();
};
let clearGlobalTimeout: Unsubscribe | null = null;

autoBeforeEachTest(() => {
	state = DEFAULT_STATE;
	DEBUG_INFO = {
		pendingIds: {},
		firstPendingCallAt: null,
		lastCompletionCallAt: null,
		addPendingSubTreeCallCount: 0,
		completeSubTreeCallCount: 0,
		unorderlyCompleteSubtreeCalls: 0,
		completionAttempts: 0,
		idleCallbackTimedOut: false,
		globalTimedOut: false,
	};

	clearNextCallback?.();
	clearNextCallback = null;
	clearGlobalTimeout?.();
	clearGlobalTimeout = null;
});

type StoreState = { isComplete: boolean; pendingIds: Record<string, string> };
const TIMEOUT_AFTER_LAST_SUBTREE_START = 5_000;
const GLOBAL_TIMEOUT = 30_000;
const actions = {
	addPendingSubtree:
		(componentId: string, name: string): Action<StoreState> =>
		({ getState, setState, dispatch }) => {
			if (!getIsHydrationInProgress()) {
				return;
			}
			DEBUG_INFO.addPendingSubTreeCallCount++;
			if (DEBUG_INFO.firstPendingCallAt == null) {
				DEBUG_INFO.firstPendingCallAt = Date.now();
			}
			if (clearGlobalTimeout == null) {
				const timerId = setTimeout(() => {
					DEBUG_INFO.globalTimedOut = true;
					dispatch(actions.complete());
				}, GLOBAL_TIMEOUT);
				clearGlobalTimeout = () => clearTimeout(timerId);
			}

			const previousPendingIds = getState().pendingIds;
			if (!Object.hasOwn(previousPendingIds, componentId)) {
				const updatedPendingIds = {
					...previousPendingIds,
					[componentId]: name,
				};
				setState({ pendingIds: updatedPendingIds });
				DEBUG_INFO.pendingIds = updatedPendingIds;

				// Fallback logic to end hydration 5s after the last pending subtree is added, if we get stuck
				setOrReplaceNextCallback(() => {
					const timerId = setTimeout(() => {
						dispatch(actions.complete());
					}, TIMEOUT_AFTER_LAST_SUBTREE_START);
					return () => clearTimeout(timerId);
				});
			}
		},
	completePendingSubtree:
		(componentId: string): Action<StoreState> =>
		({ getState, setState, dispatch }) => {
			if (!getIsHydrationInProgress()) {
				return;
			}
			DEBUG_INFO.completeSubTreeCallCount++;
			DEBUG_INFO.lastCompletionCallAt = Date.now();

			const previousPendingIds = getState().pendingIds;
			if (!Object.hasOwn(previousPendingIds, componentId)) {
				DEBUG_INFO.unorderlyCompleteSubtreeCalls++;
			}

			const updatedPendingIds = { ...previousPendingIds };
			delete updatedPendingIds[componentId];
			setState({ pendingIds: updatedPendingIds });
			DEBUG_INFO.pendingIds = updatedPendingIds;

			if (Object.keys(updatedPendingIds).length === 0) {
				DEBUG_INFO.completionAttempts++;
				setOrReplaceNextCallback(() => {
					return callWhenIdleWithTimeout((deadline) => {
						if (deadline?.didTimeout) {
							DEBUG_INFO.idleCallbackTimedOut = true;
						}
						dispatch(actions.complete());
					});
				});
			}
		},
	complete:
		(): Action<StoreState> =>
		({ setState }) => {
			if (!getIsHydrationInProgress()) {
				return;
			}
			reportHydrationEnd();
			setState({ isComplete: true });

			clearNextCallback?.();
			clearGlobalTimeout?.();

			const {
				pendingIds,
				firstPendingCallAt,
				lastCompletionCallAt,
				addPendingSubTreeCallCount,
				completeSubTreeCallCount,
				unorderlyCompleteSubtreeCalls,
				completionAttempts,
				idleCallbackTimedOut,
				globalTimedOut,
			} = DEBUG_INFO;
			const pendingSubtreeNames = Object.values(pendingIds);
			if (
				(pendingSubtreeNames.length > 0 || globalTimedOut) &&
				fg('jfp-magma-log-dirty-hydration-completion')
			) {
				let totalHydrationDuration: number | null = null;
				if (firstPendingCallAt != null && lastCompletionCallAt != null) {
					totalHydrationDuration = lastCompletionCallAt - firstPendingCallAt;
				}

				log.safeErrorWithoutCustomerData(
					'hydration-completion',
					[
						'Failed to cleanly mark hydration as complete',
						`addPendingSubTreeCallCount=${addPendingSubTreeCallCount}`,
						`completeSubTreeCallCount=${completeSubTreeCallCount}`,
						`unorderlyCompleteSubtreeCalls=${unorderlyCompleteSubtreeCalls}`,
						`globalCompletionAttempts=${completionAttempts}`,
						`idleCallbackTimedOut=${idleCallbackTimedOut}`,
						`totalHydrationDuration=${totalHydrationDuration}`,
						`globalTimedOut=${globalTimedOut}`,
						`pendingSubtreeNames: [${pendingSubtreeNames.join(', ')}]`,
					].join(', '),
				);
			}
		},
};
type Actions = typeof actions;

/**
 * We need access to global hydration state inside React (reactive) and outside React, so have both a store and module state.
 */
const store = createStore<StoreState, Actions>({
	name: 'hydration-global-completion',
	initialState: () => ({ isComplete: false, pendingIds: {} }),
	actions,
});

/**
 * Indicates if hydration has completed globally - all subtrees have completed hydrating.
 * NB: this only makes sense to request if {@link useIsHydrateRenderMode} is true.
 */
export const useIsGlobalHydrationComplete: () => boolean = createStateHook(store, {
	selector: ({ isComplete }) => isComplete,
});

export const useGlobalHydrationActions: () => {
	addPendingSubtree: (componentId: string, name: string) => void;
	completePendingSubtree: (componentId: string) => void;
} = createActionsHook(store);
